import React, { useState, useEffect, useContext } from 'react';
import { DContext } from '../../../context/DataContext';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loading from '../../blocks/Loading';
import { compressImage } from '../../../utils/compressImage';

function UpdatePackage() {
  const { t } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);
  const { id } = useParams();

  // Basic package details
  const [pkgId, setPkgId] = useState('');
  const [title, setTitle] = useState({ en: '', fr: '', ts: '' });
  const [description, setDescription] = useState({ en: '', fr: '', ts: '' });
  const [activities, setActivities] = useState({ en: '', fr: '', ts: '' });
  const [inclusion, setInclusion] = useState({ en: '', fr: '', ts: '' });
  const [whatToBring, setWhatToBring] = useState({ en: '', fr: '', ts: '' });
  const [destinations, setDestinations] = useState({ en: '', fr: '', ts: '' });
  const [isActive, setIsActive] = useState(true);
  const [popular, setPopular] = useState(false)

  // Pricing states for package type
  const [price, setPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });
  const [halfPrice, setHalfPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });
  // Pricing states for boat type
  const [speedBoatPrice, setSpeedBoatPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });
  const [longTailPrice, setLongTailPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });

  // Price type and pricing modifiers
  const [priceType, setPriceType] = useState("pkg"); // "pkg" or "boat"
  const [isHalfPrice, setIsHalfPrice] = useState(false);
  const [isLongTailPrice, setIsLongTailPrice] = useState(false);
  const [enableEntranceFees, setEnableEntranceFees] = useState(false);
  const [entranceFees, setEntranceFees] = useState({ thb: '', usd: '' });

  // Image states: 4 image files and their previews.
  const [images, setImages] = useState([null, null, null, null]);
  const [imagePreviews, setImagePreviews] = useState(["", "", "", ""]);

  // Error messages for mandatory fields.
  const [errors, setErrors] = useState({});
  const [, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch package details on mount.
  useEffect(() => {
    if (BeURL && id) {
      setLoader(true);
      fetch(`${BeURL}/fetch-package/${id}`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => {
          setLoader(false);
          if (data.success) {
            const pkg = data.package;
            setPkgId(pkg.id);
            setTitle(pkg.title);
            setDescription(pkg.description);
            setActivities({
              en: pkg.activities.en.join(', '),
              fr: pkg.activities.fr.join(', '),
              ts: pkg.activities.ts.join(', ')
            });
            setInclusion({
              en: pkg.inclusion.en.join(', '),
              fr: pkg.inclusion.fr.join(', '),
              ts: pkg.inclusion.ts.join(', ')
            });
            setWhatToBring({
              en: pkg.whatToBring.en.join(', '),
              fr: pkg.whatToBring.fr.join(', '),
              ts: pkg.whatToBring.ts.join(', ')
            });
            setDestinations({
              en: pkg.destinations.en.join(', '),
              fr: pkg.destinations.fr.join(', '),
              ts: pkg.destinations.ts.join(', ')
            });
            // Set pricing and modifiers based on priceType.
            setPriceType(pkg.priceType || "pkg");
            if (pkg.priceType === "pkg") {
              setPrice({
                thb: { kid: pkg.price.thb.kid || '', adult: pkg.price.thb.adult },
                usd: { kid: pkg.price.usd.kid || '', adult: pkg.price.usd.adult }
              });
              setIsHalfPrice(pkg.isHalfPrice);
              if (pkg.isHalfPrice && pkg.halfPrice) {
                setHalfPrice({
                  thb: { kid: pkg.halfPrice.thb.kid || '', adult: pkg.halfPrice.thb.adult },
                  usd: { kid: pkg.halfPrice.usd.kid || '', adult: pkg.halfPrice.usd.adult }
                });
              }
            } else if (pkg.priceType === "boat") {
              setSpeedBoatPrice({
                thb: { kid: pkg.speedBoatPrice.thb.kid || '', adult: pkg.speedBoatPrice.thb.adult },
                usd: { kid: pkg.speedBoatPrice.usd.kid || '', adult: pkg.speedBoatPrice.usd.adult }
              });
              setIsLongTailPrice(pkg.isLongTailPrice);
              if (pkg.isLongTailPrice && pkg.longTailPrice) {
                setLongTailPrice({
                  thb: { kid: pkg.longTailPrice.thb.kid || '', adult: pkg.longTailPrice.thb.adult },
                  usd: { kid: pkg.longTailPrice.usd.kid || '', adult: pkg.longTailPrice.usd.adult }
                });
              }
            }
            setEnableEntranceFees(pkg.isEntranceFees);
            if (pkg.isEntranceFees && pkg.entranceFees) {
              setEnableEntranceFees(true);
              setEntranceFees({
                thb: pkg.entranceFees.thb?.toString() || '',
                usd: pkg.entranceFees.usd?.toString() || ''
              });
            }
            setIsActive(pkg.isActive || false)
            setPopular(pkg.isPopular || false)
            // Images: assume pkg.images is an array of 4 image paths.
            setImagePreviews(pkg.images);
          } else {
            setMessage(data.message);
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoader(false);
          console.error("Error fetching package details:", err);
          setMessage(t('update-package.fetchError'));
          setLoading(false);
        });
    }
  }, [BeURL, id, setLoader, t]);

  // Handle file change for image updates.
  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const newFiles = [...images];
      newFiles[index] = file;
      setImages(newFiles);
      const newPreviews = [...imagePreviews];
      newPreviews[index] = URL.createObjectURL(file);
      setImagePreviews(newPreviews);
    }
  };

  // Helper to convert CSV string into an array.
  const parseCSV = (str) =>
    str.split(',').map(item => item.trim()).filter(item => item);

  // Field-level onBlur validation.
  const handleBlur = (fieldKey, value) => {
    if (!value || value.trim() === '') {
      setErrors(prev => ({ ...prev, [fieldKey]: t('create-package.requiredField') }));
    } else {
      setErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  // Overall validation on submission.
  const validateAllFields = () => {
    // Validate multi-language fields.
    for (let lang of ['en', 'fr', 'ts']) {
      if (!title[lang].trim()) return t('create-package.requiredField');
      if (!description[lang].trim()) return t('create-package.requiredField');
      if (!activities[lang].trim()) return t('create-package.requiredField');
      if (!destinations[lang].trim()) return t('create-package.requiredField');
    }
    // Pricing validations.
    if (priceType === 'pkg') {
      if (!price.thb.adult || isNaN(parseFloat(price.thb.adult))) return t('create-package.requiredField');
      if (!price.usd.adult || isNaN(parseFloat(price.usd.adult))) return t('create-package.requiredField');
      if (isHalfPrice) {
        if (!halfPrice.thb.adult || isNaN(parseFloat(halfPrice.thb.adult))) return t('create-package.requiredField');
        if (!halfPrice.usd.adult || isNaN(parseFloat(halfPrice.usd.adult))) return t('create-package.requiredField');
      }
    } else if (priceType === 'boat') {
      if (!speedBoatPrice.thb.adult || isNaN(parseFloat(speedBoatPrice.thb.adult))) return t('create-package.requiredField');
      if (!speedBoatPrice.usd.adult || isNaN(parseFloat(speedBoatPrice.usd.adult))) return t('create-package.requiredField');
      if (isLongTailPrice) {
        if (!longTailPrice.thb.adult || isNaN(parseFloat(longTailPrice.thb.adult))) return t('create-package.requiredField');
        if (!longTailPrice.usd.adult || isNaN(parseFloat(longTailPrice.usd.adult))) return t('create-package.requiredField');
      }
    }
    // Entrance fees.
    if (enableEntranceFees) {
      if (!entranceFees.thb.trim() || isNaN(parseFloat(entranceFees.thb)))
        return t('create-package.requiredField');
      if (!entranceFees.usd.trim() || isNaN(parseFloat(entranceFees.usd)))
        return t('create-package.requiredField');
    }
    // Ensure exactly 4 images (either existing or updated) are available.
    if (imagePreviews.some(preview => !preview)) return t('create-package.requiredField');
    return null;
  };

  // Form submission handler.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errorMsg = validateAllFields();
    if (errorMsg) {
      setErrors(prev => ({ ...prev, form: errorMsg }));
      return;
    }

    // Build FormData.
    const formData = new FormData();
    formData.append('title', JSON.stringify(title));
    formData.append('description', JSON.stringify(description));
    formData.append('activities', JSON.stringify({
      en: parseCSV(activities.en),
      fr: parseCSV(activities.fr),
      ts: parseCSV(activities.ts)
    }));
    formData.append('inclusion', JSON.stringify({
      en: parseCSV(inclusion.en),
      fr: parseCSV(inclusion.fr),
      ts: parseCSV(inclusion.ts)
    }));
    formData.append('whatToBring', JSON.stringify({
      en: parseCSV(whatToBring.en),
      fr: parseCSV(whatToBring.fr),
      ts: parseCSV(whatToBring.ts)
    }));
    formData.append('destinations', JSON.stringify({
      en: parseCSV(destinations.en),
      fr: parseCSV(destinations.fr),
      ts: parseCSV(destinations.ts)
    }));

    // Append pricing details based on price type.
    formData.append('priceType', priceType);
    if (priceType === 'pkg') {
      const parsedPrice = {
        thb: {
          kid: price.thb.kid ? parseFloat(price.thb.kid) : 0,
          adult: parseFloat(price.thb.adult)
        },
        usd: {
          kid: price.usd.kid ? parseFloat(price.usd.kid) : 0,
          adult: parseFloat(price.usd.adult)
        }
      };
      formData.append('price', JSON.stringify(parsedPrice));
      formData.append('isHalfPrice', isHalfPrice);
      if (isHalfPrice) {
        const parsedHalfPrice = {
          thb: {
            kid: halfPrice.thb.kid ? parseFloat(halfPrice.thb.kid) : 0,
            adult: parseFloat(halfPrice.thb.adult)
          },
          usd: {
            kid: halfPrice.usd.kid ? parseFloat(halfPrice.usd.kid) : 0,
            adult: parseFloat(halfPrice.usd.adult)
          }
        };
        formData.append('halfPrice', JSON.stringify(parsedHalfPrice));
      }
    } else if (priceType === 'boat') {
      const parsedSpeedBoatPrice = {
        thb: {
          kid: speedBoatPrice.thb.kid ? parseFloat(speedBoatPrice.thb.kid) : 0,
          adult: parseFloat(speedBoatPrice.thb.adult)
        },
        usd: {
          kid: speedBoatPrice.usd.kid ? parseFloat(speedBoatPrice.usd.kid) : 0,
          adult: parseFloat(speedBoatPrice.usd.adult)
        }
      };
      formData.append('speedBoatPrice', JSON.stringify(parsedSpeedBoatPrice));
      formData.append('isLongTailPrice', isLongTailPrice);
      if (isLongTailPrice) {
        const parsedLongTailPrice = {
          thb: {
            kid: longTailPrice.thb.kid ? parseFloat(longTailPrice.thb.kid) : 0,
            adult: parseFloat(longTailPrice.thb.adult)
          },
          usd: {
            kid: longTailPrice.usd.kid ? parseFloat(longTailPrice.usd.kid) : 0,
            adult: parseFloat(longTailPrice.usd.adult)
          }
        };
        formData.append('longTailPrice', JSON.stringify(parsedLongTailPrice));
      }
    }
    formData.append('isEntranceFees', enableEntranceFees);
    if (enableEntranceFees) {
      formData.append(
        'entranceFees',
        JSON.stringify({
          thb: parseFloat(entranceFees.thb),
          usd: parseFloat(entranceFees.usd)
        })
      );
    }

    formData.append('isActive', isActive);
    formData.append('popular', popular);


    // Append images if new files are selected.
    // (If an image is not updated, the backend should continue using the current image path.)
    for (let index = 0; index < images.length; index += 1) {
      const file = images[index];
      if (file) {
        const compressedFile = await compressImage(file, { maxSizeMB: 1, maxWidthOrHeight: 1920 });
        formData.append(`image${index}`, compressedFile);
      }
    }

    try {
      setLoader(true);
      const res = await fetch(`${BeURL}/update-package/${id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });
      const data = await res.json();
      setLoader(false);
      if (data.success) {
        alert(t('update-package.success'));
        window.location.href = "/admin/manage-packages";
      } else {
        setErrors(prev => ({ ...prev, form: data.message || t('update-package.failure') }));
      }
    } catch (error) {
      console.error("Error updating package:", error);
      setLoader(false);
      setErrors(prev => ({ ...prev, form: t('update-package.failure') }));
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="mx-auto my-2 max-w-7xl">
      <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-[#1739bd]">{t('update-package.heading')}</h2>
      {errors.form && <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{errors.form}</div>}
      <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
        {/* Read-Only Package ID */}
        <div className="mb-3">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.packageId')}</label>
          <input type="text" className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring" value={pkgId} readOnly />
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {/* Title Fields */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`title-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                {t('update-package.title')} ({lang.toUpperCase()}) *
              </label>
              <input
                type="text"
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                value={title[lang]}
                onChange={e => {
                  setTitle({ ...title, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`title_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`title_${lang}`, e.target.value)}
                required
              />
              {errors[`title_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`title_${lang}`]}</small>}
            </div>
          ))}

          {/* Description Fields */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`description-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                {t('update-package.description')} ({lang.toUpperCase()}) *
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                value={description[lang]}
                onChange={e => {
                  setDescription({ ...description, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`description_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`description_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`description_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`description_${lang}`]}</small>}
            </div>
          ))}

          {/* Activities Fields */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`activities-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                {t('update-package.activities')} ({lang.toUpperCase()}) *
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('update-package.enterCommaSeparated')}
                value={activities[lang]}
                onChange={e => {
                  setActivities({ ...activities, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`activities_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`activities_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`activities_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`activities_${lang}`]}</small>}
            </div>
          ))}

          {/* Inclusion Fields (Optional) */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`inclusion-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.inclusion')} ({lang.toUpperCase()})</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('update-package.enterCommaSeparated')}
                value={inclusion[lang]}
                onChange={e => setInclusion({ ...inclusion, [lang]: e.target.value })}
              ></textarea>
            </div>
          ))}

          {/* What To Bring Fields (Optional) */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`whatToBring-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.whatToBring')} ({lang.toUpperCase()})</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('update-package.enterCommaSeparated')}
                value={whatToBring[lang]}
                onChange={e => setWhatToBring({ ...whatToBring, [lang]: e.target.value })}
              ></textarea>
            </div>
          ))}

          {/* Destinations Fields */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`destinations-${lang}`} className="w-full" style={{ minWidth: '300px' }}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                {t('update-package.destinations')} ({lang.toUpperCase()}) *
              </label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('update-package.enterCommaSeparated')}
                value={destinations[lang]}
                onChange={e => {
                  setDestinations({ ...destinations, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`destinations_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`destinations_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`destinations_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`destinations_${lang}`]}</small>}
            </div>
          ))}

          {/* Pricing Section */}
          <div className="w-full mt-3">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.priceType')}</label>
            <div>
              <label className="me-3">
                <input
                  type="radio"
                  name="priceType"
                  value="pkg"
                  checked={priceType === "pkg"}
                  onChange={() => setPriceType("pkg")}
                /> {t('update-package.package')}
              </label>
              <label>
                <input
                  type="radio"
                  name="priceType"
                  value="boat"
                  checked={priceType === "boat"}
                  onChange={() => setPriceType("boat")}
                /> {t('update-package.boat')}
              </label>
            </div>
          </div>

          {/* Price fields for Package type */}
          {priceType === "pkg" && (
            <div className="w-full mt-3">
              <h4>{t('update-package.packagePrice')}</h4>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.priceTHBAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.thb.adult}
                    onChange={e => {
                      setPrice({ ...price, thb: { ...price.thb, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'price_pkg_thb_adult': '' }));
                    }}
                    onBlur={e => handleBlur('price_pkg_thb_adult', e.target.value)}
                    required
                  />
                  {errors['price_pkg_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['price_pkg_thb_adult']}</small>}
                </div>
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.priceUSDAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.usd.adult}
                    onChange={e => {
                      setPrice({ ...price, usd: { ...price.usd, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'price_pkg_usd_adult': '' }));
                    }}
                    onBlur={e => handleBlur('price_pkg_usd_adult', e.target.value)}
                    required
                  />
                  {errors['price_pkg_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['price_pkg_usd_adult']}</small>}
                </div>
                {/* Optional Kid Prices */}
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.priceTHBKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.thb.kid}
                    onChange={e => setPrice({ ...price, thb: { ...price.thb, kid: e.target.value } })}
                  />
                </div>
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.priceUSDKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.usd.kid}
                    onChange={e => setPrice({ ...price, usd: { ...price.usd, kid: e.target.value } })}
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                  id="halfPriceCheckbox"
                  checked={isHalfPrice}
                  onChange={() => setIsHalfPrice(!isHalfPrice)}
                />
                <label className="text-sm font-medium text-slate-700" htmlFor="halfPriceCheckbox">
                  {t('update-package.enableHalfPrice')}
                </label>
              </div>
              {isHalfPrice && (
                <div className="mt-2 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.halfPriceTHBAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.thb.adult}
                      onChange={e => {
                        setHalfPrice({ ...halfPrice, thb: { ...halfPrice.thb, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'halfPrice_pkg_thb_adult': '' }));
                      }}
                      onBlur={e => handleBlur('halfPrice_pkg_thb_adult', e.target.value)}
                      required
                    />
                    {errors['halfPrice_pkg_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['halfPrice_pkg_thb_adult']}</small>}
                  </div>
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.halfPriceUSDAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.usd.adult}
                      onChange={e => {
                        setHalfPrice({ ...halfPrice, usd: { ...halfPrice.usd, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'halfPrice_pkg_usd_adult': '' }));
                      }}
                      onBlur={e => handleBlur('halfPrice_pkg_usd_adult', e.target.value)}
                      required
                    />
                    {errors['halfPrice_pkg_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['halfPrice_pkg_usd_adult']}</small>}
                  </div>
                  {/* Optional Kid Prices for halfPrice */}
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.halfPriceTHBKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.thb.kid}
                      onChange={e => setHalfPrice({ ...halfPrice, thb: { ...halfPrice.thb, kid: e.target.value } })}
                    />
                  </div>
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.halfPriceUSDKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.usd.kid}
                      onChange={e => setHalfPrice({ ...halfPrice, usd: { ...halfPrice.usd, kid: e.target.value } })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price fields for Boat type */}
          {priceType === "boat" && (
            <div className="w-full mt-3">
              <h4>{t('update-package.speedBoatPrice')}</h4>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.speedBoatPriceTHBAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.thb.adult}
                    onChange={e => {
                      setSpeedBoatPrice({ ...speedBoatPrice, thb: { ...speedBoatPrice.thb, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'speedBoat_thb_adult': '' }));
                    }}
                    onBlur={e => handleBlur('speedBoat_thb_adult', e.target.value)}
                    required
                  />
                  {errors['speedBoat_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['speedBoat_thb_adult']}</small>}
                </div>
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.speedBoatPriceUSDAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.usd.adult}
                    onChange={e => {
                      setSpeedBoatPrice({ ...speedBoatPrice, usd: { ...speedBoatPrice.usd, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'speedBoat_usd_adult': '' }));
                    }}
                    onBlur={e => handleBlur('speedBoat_usd_adult', e.target.value)}
                    required
                  />
                  {errors['speedBoat_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['speedBoat_usd_adult']}</small>}
                </div>
                {/* Optional Kid Prices for speed boat */}
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.speedBoatPriceTHBKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.thb.kid}
                    onChange={e => setSpeedBoatPrice({ ...speedBoatPrice, thb: { ...speedBoatPrice.thb, kid: e.target.value } })}
                  />
                </div>
                <div className="w-full" style={{ minWidth: '300px' }}>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.speedBoatPriceUSDKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.usd.kid}
                    onChange={e => setSpeedBoatPrice({ ...speedBoatPrice, usd: { ...speedBoatPrice.usd, kid: e.target.value } })}
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                  id="longTailPriceCheckbox"
                  checked={isLongTailPrice}
                  onChange={() => setIsLongTailPrice(!isLongTailPrice)}
                />
                <label className="text-sm font-medium text-slate-700" htmlFor="longTailPriceCheckbox">
                  {t('update-package.enableLongTailPrice')}
                </label>
              </div>
              {isLongTailPrice && (
                <div className="mt-2 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.longTailPriceTHBAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.thb.adult}
                      onChange={e => {
                        setLongTailPrice({ ...longTailPrice, thb: { ...longTailPrice.thb, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'longTail_thb_adult': '' }));
                      }}
                      onBlur={e => handleBlur('longTail_thb_adult', e.target.value)}
                      required
                    />
                    {errors['longTail_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['longTail_thb_adult']}</small>}
                  </div>
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.longTailPriceUSDAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.usd.adult}
                      onChange={e => {
                        setLongTailPrice({ ...longTailPrice, usd: { ...longTailPrice.usd, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'longTail_usd_adult': '' }));
                      }}
                      onBlur={e => handleBlur('longTail_usd_adult', e.target.value)}
                      required
                    />
                    {errors['longTail_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['longTail_usd_adult']}</small>}
                  </div>
                  {/* Optional Kid Prices for longtail */}
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.longTailPriceTHBKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.thb.kid}
                      onChange={e => setLongTailPrice({ ...longTailPrice, thb: { ...longTailPrice.thb, kid: e.target.value } })}
                    />
                  </div>
                  <div className="w-full" style={{ minWidth: '300px' }}>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.longTailPriceUSDKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.usd.kid}
                      onChange={e => setLongTailPrice({ ...longTailPrice, usd: { ...longTailPrice.usd, kid: e.target.value } })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Entrance Fees Section */}
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
              id="entranceFeesCheckbox"
              checked={enableEntranceFees}
              onChange={() => setEnableEntranceFees(!enableEntranceFees)}
            />
            <label className="text-sm font-medium text-slate-700" htmlFor="entranceFeesCheckbox">
              {t('update-package.enableEntranceFees')}
            </label>
          </div>
          {enableEntranceFees && (
            <div className="mt-2 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div className="w-full" style={{ minWidth: '300px' }}>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.entranceFees')} (THB) *</label>
                <input
                  type="number"
                  className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                  value={entranceFees.thb}
                  onChange={e => setEntranceFees({ ...entranceFees, thb: e.target.value })}
                  onBlur={e => handleBlur('entranceFees_thb', e.target.value)}
                  required
                />
                {errors['entranceFees_thb'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['entranceFees_thb']}</small>}
              </div>
              <div className="w-full" style={{ minWidth: '300px' }}>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.entranceFees')} (USD) *</label>
                <input
                  type="number"
                  className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                  value={entranceFees.usd}
                  onChange={e => setEntranceFees({ ...entranceFees, usd: e.target.value })}
                  onBlur={e => handleBlur('entranceFees_usd', e.target.value)}
                  required
                />
                {errors['entranceFees_usd'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['entranceFees_usd']}</small>}
              </div>
            </div>
          )}

          {/* isActive Checkbox */}
          <div className="w-full">
            <div className="my-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                id="isActiveCheckbox"
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
              />
              <label className="text-sm font-medium text-slate-700" htmlFor="isActiveCheckbox">
                {t('update-package.isActive')}
              </label>
            </div>
            <div className="my-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                id="popular"
                checked={popular}
                onChange={() => setPopular(!popular)}
              />
              <label className="text-sm font-medium text-slate-700" htmlFor="popular">
                {t('create-package.popular')}
              </label>
            </div>
          </div>


          {/* Image Upload Fields */}
          <div className="w-full mt-3">
            <label className="mb-1.5 block py-2 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.images')} *</label>
            <div className="grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map(index => (
                <div key={index} className="relative" style={{ width: '150px', height: '150px' }}>
                  {(() => {
                    const preview = imagePreviews[index];
                    const imageSrc = images[index]
                      ? preview
                      : (String(preview || '').startsWith('http://') || String(preview || '').startsWith('https://')
                        ? preview
                        : `${BeURL}/${preview}`);
                    return (
                      <img
                        src={imageSrc}
                        alt={`Preview ${index + 1}`}
                        className="h-full w-full rounded-xl border border-slate-200 object-cover"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    );
                  })()}
                  <input
                    type="file"
                    className="absolute inset-0 h-full w-full opacity-0"
                    accept="image/*"
                    onChange={e => handleImageChange(index, e)}
                  />
                </div>
              ))}
              {errors['images'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['images']}</small>}
            </div>
          </div>

        </div>

        <div className="mt-6 text-center">
          <button type="submit" className="inline-flex items-center rounded-2xl bg-[#1739bd] px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
            {t('update-package.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UpdatePackage;

